import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Empty,
  Input,
  List,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Tabs,
  Typography,
} from '@arco-design/web-react';
import { IconClose } from '@arco-design/web-react/icon';
import type { Task } from '@/types/task';
import { TASK_STATUS_COLOR, TASK_STATUS_TEXT } from '@/types/task';
import type { Project } from '@/types/project';
import IconProject from '@/assets/project.svg';
import IconPiecework from '@/assets/piecework.svg';
import IconOffshore from '@/assets/offshore.svg';
import IconOnsite from '@/assets/onsite.svg';
import {
  getGlobalSearchAll,
  getGlobalSearchProjects,
  getGlobalSearchTasks,
  type GlobalSearchAllData,
} from '@/api/global-search';
import UserAvatar from '@/components/UserAvatar';
import { createLogger } from '@/utils/logger';
import styles from './style/global-search-modal.module.less';

const logger = createLogger('GlobalSearchModal');
const { TabPane } = Tabs;
const DEBOUNCE_MS = 200;
const PAGE_SIZE = 10;
const CACHE_TTL_MS = 30 * 1000;
const PRELOAD_ROOT_MARGIN = '240px';
const MAX_INITIAL_AUTO_LOAD_PAGES = 6;

type SearchTab = 'all' | 'tasks' | 'projects';

interface SearchCacheEntry<T> {
  expiresAt: number;
  data: T;
}

interface PagedResult<T> {
  items: T[];
  total: number;
  current: number;
  hasMore: boolean;
}

interface GlobalSearchModalProps {
  visible: boolean;
  onClose: () => void;
  onTaskClick: (task: Task) => void;
  onProjectClick: (project: Project) => void;
}

function isCanceledError(error: unknown): boolean {
  const err = error as { name?: string; code?: string };
  return err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED';
}

function getTaskStatusLabel(status: Task['status']): string {
  return TASK_STATUS_TEXT[status];
}

function getTaskStatusColor(status: Task['status']): 'green' | 'blue' {
  return TASK_STATUS_COLOR[status] === 'green' ? 'green' : 'blue';
}

function getTaskProjectText(task: Task): string {
  if (task.projectType === '计件制') {
    const framework = (task.frameworkName || task.projectName || '').trim();
    const requirement = (task.requirementName || '').trim();
    if (framework && requirement) {
      return `${framework} / ${requirement}`;
    }
    return framework || requirement || '未命名项目';
  }
  return task.projectName || '未命名项目';
}

function getTaskProjectFilterLabel(task: Task): string {
  if (task.projectType === '计件制') {
    return (task.frameworkName || task.projectName || '').trim() || '未命名项目';
  }
  return task.projectName || '未命名项目';
}

function getTaskProjectFilterValue(task: Task): string {
  if (task.projectType === '计件制' && task.frameworkName?.trim()) {
    return `framework:${task.frameworkName.trim()}`;
  }
  return `project:${task.projectId}`;
}

function getProjectTypeIcon(type?: string): React.ReactNode {
  if (type === '计件制') return <IconPiecework />;
  if (type === '离岸制') return <IconOffshore />;
  if (type === '驻场制') return <IconOnsite />;
  return <IconProject />;
}

function getProjectTypeIconClass(type?: string): string {
  if (type === '计件制') return styles.iconPiecework;
  if (type === '离岸制') return styles.iconOffshore;
  if (type === '驻场制') return styles.iconOnsite;
  return styles.iconProject;
}

function buildCacheKey(tab: SearchTab, keyword: string, page = 1, extra = ''): string {
  return `${tab}|${keyword}|${extra}|${page}`;
}

function readCache<T>(cache: Map<string, SearchCacheEntry<unknown>>, key: string): T | null {
  const entry = cache.get(key);
  if (!entry) {
    return null;
  }
  if (entry.expiresAt <= Date.now()) {
    cache.delete(key);
    return null;
  }
  return entry.data as T;
}

function writeCache<T>(cache: Map<string, SearchCacheEntry<unknown>>, key: string, data: T): void {
  cache.set(key, {
    data,
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
}

export default function GlobalSearchModal({
  visible,
  onClose,
  onTaskClick,
  onProjectClick,
}: GlobalSearchModalProps) {
  const [tab, setTab] = useState<SearchTab>('all');
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [loadingAll, setLoadingAll] = useState(false);
  const [allData, setAllData] = useState<GlobalSearchAllData>({
    tasks: [],
    projects: [],
    tasksTotal: 0,
    projectsTotal: 0,
  });

  const [taskData, setTaskData] = useState<PagedResult<Task>>({
    items: [],
    total: 0,
    current: 1,
    hasMore: false,
  });
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskLoadingMore, setTaskLoadingMore] = useState(false);

  const [projectData, setProjectData] = useState<PagedResult<Project>>({
    items: [],
    total: 0,
    current: 1,
    hasMore: false,
  });
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectLoadingMore, setProjectLoadingMore] = useState(false);
  const [taskProjectFilter, setTaskProjectFilter] = useState<string>();
  const [taskStatusFilter, setTaskStatusFilter] = useState<Task['status']>();
  const [taskAssigneeFilter, setTaskAssigneeFilter] = useState<string>();

  const cacheRef = useRef<Map<string, SearchCacheEntry<unknown>>>(new Map());
  const inputRef = useRef<React.ComponentRef<typeof Input> | null>(null);
  const taskScrollRef = useRef<HTMLDivElement | null>(null);
  const taskSentinelRef = useRef<HTMLDivElement | null>(null);
  const projectScrollRef = useRef<HTMLDivElement | null>(null);
  const projectSentinelRef = useRef<HTMLDivElement | null>(null);
  const taskPageRef = useRef(1);
  const projectPageRef = useRef(1);
  const taskUserScrolledRef = useRef(false);
  const projectUserScrolledRef = useRef(false);
  const taskInitialAutoLoadCountRef = useRef(0);
  const projectInitialAutoLoadCountRef = useRef(0);

  const controllersRef = useRef<Record<SearchTab, AbortController | null>>({
    all: null,
    tasks: null,
    projects: null,
  });
  const seqRef = useRef<Record<SearchTab, number>>({
    all: 0,
    tasks: 0,
    projects: 0,
  });

  useEffect(() => {
    if (!visible) {
      return;
    }
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [visible]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedKeyword(keyword.trim());
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [keyword]);

  const closeTabRequest = useCallback((targetTab: SearchTab) => {
    controllersRef.current[targetTab]?.abort();
    controllersRef.current[targetTab] = null;
  }, []);

  const taskFilterKey = useMemo(
    () => `${taskProjectFilter || ''}|${taskStatusFilter || ''}|${taskAssigneeFilter || ''}`,
    [taskProjectFilter, taskStatusFilter, taskAssigneeFilter]
  );

  const taskFilterSource = useMemo(
    () => [...allData.tasks, ...taskData.items],
    [allData.tasks, taskData.items]
  );

  const taskProjectOptions = useMemo(() => {
    const map = new Map<string, string>();
    taskFilterSource.forEach((task) => {
      const value = getTaskProjectFilterValue(task);
      if (!map.has(value)) {
        map.set(value, getTaskProjectFilterLabel(task));
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [taskFilterSource]);

  const taskAssigneeOptions = useMemo(() => {
    const map = new Map<string, string>();
    taskFilterSource.forEach((task) => {
      if (task.assigneeId && task.assigneeName) {
        map.set(task.assigneeId, task.assigneeName);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [taskFilterSource]);

  const fetchAll = useCallback(async () => {
    const cacheKey = buildCacheKey('all', debouncedKeyword);
    const cached = readCache<GlobalSearchAllData>(cacheRef.current, cacheKey);
    if (cached) {
      setAllData(cached);
      return;
    }

    closeTabRequest('all');
    const controller = new AbortController();
    controllersRef.current.all = controller;
    const currentSeq = seqRef.current.all + 1;
    seqRef.current.all = currentSeq;

    setLoadingAll(true);

    try {
      const result = await getGlobalSearchAll(
        { keyword: debouncedKeyword || undefined },
        { signal: controller.signal }
      );

      if (seqRef.current.all !== currentSeq) {
        return;
      }

      setAllData(result);
      writeCache(cacheRef.current, cacheKey, result);
    } catch (error) {
      if (isCanceledError(error)) {
        return;
      }
      logger.error('加载全局搜索摘要失败', { error });
      setAllData({ tasks: [], projects: [], tasksTotal: 0, projectsTotal: 0 });
    } finally {
      if (seqRef.current.all === currentSeq) {
        setLoadingAll(false);
      }
    }
  }, [debouncedKeyword, closeTabRequest]);

  const fetchTasks = useCallback(
    async (reset: boolean) => {
      const targetPage = reset ? 1 : taskPageRef.current + 1;
      if (reset) {
        taskUserScrolledRef.current = false;
        taskInitialAutoLoadCountRef.current = 0;
      }
      const cacheKey = buildCacheKey('tasks', debouncedKeyword, targetPage, taskFilterKey);
      const cached = readCache<{ data: Task[]; total: number; hasMore: boolean }>(
        cacheRef.current,
        cacheKey
      );

      if (cached) {
        taskPageRef.current = targetPage;
        setTaskData((prev) => ({
          items: reset ? cached.data : [...prev.items, ...cached.data],
          total: cached.total,
          current: targetPage,
          hasMore: cached.hasMore,
        }));
        return;
      }

      closeTabRequest('tasks');
      const controller = new AbortController();
      controllersRef.current.tasks = controller;
      const currentSeq = seqRef.current.tasks + 1;
      seqRef.current.tasks = currentSeq;

      if (reset) {
        setTaskLoading(true);
      } else {
        setTaskLoadingMore(true);
      }

      try {
        const projectId = taskProjectFilter?.startsWith('project:')
          ? taskProjectFilter.slice('project:'.length)
          : undefined;
        const frameworkName = taskProjectFilter?.startsWith('framework:')
          ? taskProjectFilter.slice('framework:'.length)
          : undefined;

        const result = await getGlobalSearchTasks(
          {
            keyword: debouncedKeyword || undefined,
            current: targetPage,
            pageSize: PAGE_SIZE,
            projectId,
            frameworkName,
            status: taskStatusFilter,
            assigneeId: taskAssigneeFilter,
          },
          { signal: controller.signal }
        );

        if (seqRef.current.tasks !== currentSeq) {
          return;
        }

        taskPageRef.current = targetPage;
        setTaskData((prev) => ({
          items: reset ? result.data : [...prev.items, ...result.data],
          total: result.total,
          current: targetPage,
          hasMore: result.hasMore,
        }));

        writeCache(cacheRef.current, cacheKey, {
          data: result.data,
          total: result.total,
          hasMore: result.hasMore,
        });
      } catch (error) {
        if (isCanceledError(error)) {
          return;
        }
        logger.error('加载任务搜索结果失败', { error });
        if (reset) {
          taskPageRef.current = 1;
          setTaskData({ items: [], total: 0, current: 1, hasMore: false });
        }
      } finally {
        if (seqRef.current.tasks === currentSeq) {
          if (reset) {
            setTaskLoading(false);
          } else {
            setTaskLoadingMore(false);
          }
        }
      }
    },
    [debouncedKeyword, closeTabRequest, taskFilterKey, taskProjectFilter, taskStatusFilter, taskAssigneeFilter]
  );

  const fetchProjects = useCallback(
    async (reset: boolean) => {
      const targetPage = reset ? 1 : projectPageRef.current + 1;
      if (reset) {
        projectUserScrolledRef.current = false;
        projectInitialAutoLoadCountRef.current = 0;
      }
      const cacheKey = buildCacheKey('projects', debouncedKeyword, targetPage);
      const cached = readCache<{ data: Project[]; total: number; hasMore: boolean }>(
        cacheRef.current,
        cacheKey
      );

      if (cached) {
        projectPageRef.current = targetPage;
        setProjectData((prev) => ({
          items: reset ? cached.data : [...prev.items, ...cached.data],
          total: cached.total,
          current: targetPage,
          hasMore: cached.hasMore,
        }));
        return;
      }

      closeTabRequest('projects');
      const controller = new AbortController();
      controllersRef.current.projects = controller;
      const currentSeq = seqRef.current.projects + 1;
      seqRef.current.projects = currentSeq;

      if (reset) {
        setProjectLoading(true);
      } else {
        setProjectLoadingMore(true);
      }

      try {
        const result = await getGlobalSearchProjects(
          {
            keyword: debouncedKeyword || undefined,
            current: targetPage,
            pageSize: PAGE_SIZE,
          },
          { signal: controller.signal }
        );

        if (seqRef.current.projects !== currentSeq) {
          return;
        }

        projectPageRef.current = targetPage;
        setProjectData((prev) => ({
          items: reset ? result.data : [...prev.items, ...result.data],
          total: result.total,
          current: targetPage,
          hasMore: result.hasMore,
        }));

        writeCache(cacheRef.current, cacheKey, {
          data: result.data,
          total: result.total,
          hasMore: result.hasMore,
        });
      } catch (error) {
        if (isCanceledError(error)) {
          return;
        }
        logger.error('加载项目搜索结果失败', { error });
        if (reset) {
          projectPageRef.current = 1;
          setProjectData({ items: [], total: 0, current: 1, hasMore: false });
        }
      } finally {
        if (seqRef.current.projects === currentSeq) {
          if (reset) {
            setProjectLoading(false);
          } else {
            setProjectLoadingMore(false);
          }
        }
      }
    },
    [debouncedKeyword, closeTabRequest]
  );

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (tab === 'all') {
      fetchAll();
      return;
    }

    if (tab === 'tasks') {
      fetchTasks(true);
      return;
    }

    fetchProjects(true);
  }, [visible, tab, debouncedKeyword, fetchAll, fetchTasks, fetchProjects]);

  useEffect(() => {
    if (!visible || tab !== 'tasks') {
      return;
    }

    const root = taskScrollRef.current;
    const target = taskSentinelRef.current;
    if (!root || !target) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) {
          return;
        }
        if (!taskData.hasMore || taskLoading || taskLoadingMore) {
          return;
        }

        const rootEl = taskScrollRef.current;
        const isScrollable = Boolean(
          rootEl && rootEl.scrollHeight > rootEl.clientHeight + 1
        );
        const isInitialAutoLoad = !taskUserScrolledRef.current;
        if (isInitialAutoLoad) {
          const allowAutoPages = isScrollable ? 1 : MAX_INITIAL_AUTO_LOAD_PAGES;
          if (taskInitialAutoLoadCountRef.current >= allowAutoPages) {
            return;
          }
          taskInitialAutoLoadCountRef.current += 1;
        }

        fetchTasks(false);
      },
      {
        root,
        rootMargin: PRELOAD_ROOT_MARGIN,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [visible, tab, taskData.hasMore, taskLoading, taskLoadingMore, fetchTasks]);

  useEffect(() => {
    if (!visible || tab !== 'projects') {
      return;
    }

    const root = projectScrollRef.current;
    const target = projectSentinelRef.current;
    if (!root || !target) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) {
          return;
        }
        if (!projectData.hasMore || projectLoading || projectLoadingMore) {
          return;
        }

        const rootEl = projectScrollRef.current;
        const isScrollable = Boolean(
          rootEl && rootEl.scrollHeight > rootEl.clientHeight + 1
        );
        const isInitialAutoLoad = !projectUserScrolledRef.current;
        if (isInitialAutoLoad) {
          const allowAutoPages = isScrollable ? 1 : MAX_INITIAL_AUTO_LOAD_PAGES;
          if (projectInitialAutoLoadCountRef.current >= allowAutoPages) {
            return;
          }
          projectInitialAutoLoadCountRef.current += 1;
        }

        fetchProjects(false);
      },
      {
        root,
        rootMargin: PRELOAD_ROOT_MARGIN,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [visible, tab, projectData.hasMore, projectLoading, projectLoadingMore, fetchProjects]);

  useEffect(() => {
    if (!visible) {
      closeTabRequest('all');
      closeTabRequest('tasks');
      closeTabRequest('projects');

      setTab('all');
      setKeyword('');
      setDebouncedKeyword('');
      setTaskProjectFilter(undefined);
      setTaskStatusFilter(undefined);
      setTaskAssigneeFilter(undefined);
      setLoadingAll(false);
      setTaskLoading(false);
      setTaskLoadingMore(false);
      setProjectLoading(false);
      setProjectLoadingMore(false);

      setAllData({
        tasks: [],
        projects: [],
        tasksTotal: 0,
        projectsTotal: 0,
      });
      setTaskData({
        items: [],
        total: 0,
        current: 1,
        hasMore: false,
      });
      setProjectData({
        items: [],
        total: 0,
        current: 1,
        hasMore: false,
      });

      taskPageRef.current = 1;
      projectPageRef.current = 1;
      taskUserScrolledRef.current = false;
      projectUserScrolledRef.current = false;
      taskInitialAutoLoadCountRef.current = 0;
      projectInitialAutoLoadCountRef.current = 0;

      if (taskScrollRef.current) {
        taskScrollRef.current.scrollTop = 0;
      }
      if (projectScrollRef.current) {
        projectScrollRef.current.scrollTop = 0;
      }

      return;
    }
  }, [visible, closeTabRequest]);

  const renderTaskItem = useCallback(
    (task: Task) => (
      <List.Item
        key={task.id}
        className={styles.clickableItem}
        extra={(
          <Space size={8} className={styles.itemExtra}>
            <UserAvatar
              size={24}
              name={task.assigneeName || '未分配'}
              avatarUrl={task.assigneeAvatar || null}
            />
            <Typography.Text type="secondary" className={styles.assigneeText}>
              {task.assigneeName || '未分配'}
            </Typography.Text>
          </Space>
        )}
        onClick={() => onTaskClick(task)}
      >
        <List.Item.Meta
          title={(
            <Space size={8} className={styles.itemTitle}>
              <Tag size="small" color={getTaskStatusColor(task.status)} className={styles.statusTag}>
                {getTaskStatusLabel(task.status)}
              </Tag>
              <Typography.Text
                className={`${styles.ellipsisText} ${styles.taskTitleText}`}
                style={{ marginBottom: 0 }}
                ellipsis={{ showTooltip: true }}
              >
                {task.name || '未命名任务'}
              </Typography.Text>
            </Space>
          )}
          description={(
            <Typography.Text type="secondary" className={styles.ellipsisText}>
              {getTaskProjectText(task)}
            </Typography.Text>
          )}
        />
      </List.Item>
    ),
    [onTaskClick]
  );

  const renderProjectItem = useCallback(
    (project: Project) => (
      <List.Item
        key={project.id}
        className={styles.clickableItem}
        onClick={() => onProjectClick(project)}
      >
        <List.Item.Meta
          avatar={(
            <span className={`${styles.typeIcon} ${getProjectTypeIconClass(project.type)}`}>
              {getProjectTypeIcon(project.type)}
            </span>
          )}
          title={(
            <Typography.Text
              className={styles.ellipsisText}
              style={{ marginBottom: 0 }}
              ellipsis={{ showTooltip: true }}
            >
              {project.name || '未命名项目'}
            </Typography.Text>
          )}
        />
      </List.Item>
    ),
    [onProjectClick]
  );

  function handleSwitchTab(nextTab: SearchTab): void {
    setDebouncedKeyword(keyword.trim());
    setTab(nextTab);
  }

  const handleTaskScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if ((event.currentTarget as HTMLDivElement).scrollTop > 0) {
      taskUserScrolledRef.current = true;
    }
  }, []);

  const handleProjectScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if ((event.currentTarget as HTMLDivElement).scrollTop > 0) {
      projectUserScrolledRef.current = true;
    }
  }, []);

  return (
    <Modal
      title={null}
      visible={visible}
      footer={null}
      onCancel={onClose}
      closable={false}
      className={styles.modal}
      wrapClassName={styles.modalWrap}
      style={{ width: 720 }}
      unmountOnExit
    >
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <Input.Search
            ref={inputRef}
            value={keyword}
            onChange={setKeyword}
            placeholder="搜索项目 / 任务"
            allowClear
            searchButton={false}
            className={styles.searchInput}
          />
          <Button
            type="text"
            icon={<IconClose />}
            onClick={onClose}
            className={styles.closeButton}
            aria-label="关闭搜索弹窗"
          />
        </div>

        <Tabs activeTab={tab} onChange={(value) => handleSwitchTab(value as SearchTab)} className={styles.tabs}>
          <TabPane key="all" title="全部">
            {loadingAll ? (
              <div className={styles.centerSpin}>
                <Spin />
              </div>
            ) : (
              <div className={styles.allGrid}>
                <Card
                  size="small"
                  className={styles.summaryCard}
                  title="任务"
                  extra={<Typography.Text type="secondary">共 {allData.tasksTotal} 条</Typography.Text>}
                >
                  {allData.tasks.length === 0 ? (
                    <Empty description="暂无任务" className={styles.empty} />
                  ) : (
                    <List
                      bordered={false}
                      hoverable
                      className={styles.innerList}
                      dataSource={allData.tasks}
                      render={renderTaskItem}
                    />
                  )}
                  <div className={styles.cardAction}>
                    <Button type="text" size="small" onClick={() => handleSwitchTab('tasks')}>
                      加载更多
                    </Button>
                  </div>
                </Card>

                <Card
                  size="small"
                  className={styles.summaryCard}
                  title="项目"
                  extra={<Typography.Text type="secondary">共 {allData.projectsTotal} 条</Typography.Text>}
                >
                  {allData.projects.length === 0 ? (
                    <Empty description="暂无项目" className={styles.empty} />
                  ) : (
                    <List
                      bordered={false}
                      hoverable
                      className={styles.innerList}
                      dataSource={allData.projects}
                      render={renderProjectItem}
                    />
                  )}
                  <div className={styles.cardAction}>
                    <Button type="text" size="small" onClick={() => handleSwitchTab('projects')}>
                      加载更多
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </TabPane>

          <TabPane key="tasks" title="任务">
            <div className={styles.taskTabContent}>
              <div className={styles.taskFilters}>
                <Select
                  allowClear
                  showSearch
                  placeholder="选择项目"
                  className={styles.taskFilterSelect}
                  value={taskProjectFilter}
                  options={taskProjectOptions}
                  onChange={(value) => setTaskProjectFilter((value as string) || undefined)}
                />
                <Select
                  allowClear
                  placeholder="任务状态"
                  className={styles.taskFilterSelect}
                  value={taskStatusFilter}
                  options={[
                    { label: '进行中', value: 'in_progress' },
                    { label: '已完成', value: 'completed' },
                  ]}
                  onChange={(value) => setTaskStatusFilter((value as Task['status']) || undefined)}
                />
                <Select
                  allowClear
                  showSearch
                  placeholder="任务执行者"
                  className={styles.taskFilterSelect}
                  value={taskAssigneeFilter}
                  options={taskAssigneeOptions}
                  onChange={(value) => setTaskAssigneeFilter((value as string) || undefined)}
                />
              </div>
              <div ref={taskScrollRef} className={styles.scrollPanel} onScroll={handleTaskScroll}>
                {taskLoading ? (
                  <div className={styles.centerSpin}>
                    <Spin />
                  </div>
                ) : taskData.items.length === 0 ? (
                  <Empty description="暂无任务" className={styles.empty} />
                ) : (
                  <>
                    <List
                      bordered={false}
                      hoverable
                      dataSource={taskData.items}
                      render={renderTaskItem}
                    />
                    <div ref={taskSentinelRef} className={styles.sentinel} />
                    {taskLoadingMore ? (
                      <div className={styles.loadingMore}>加载中...</div>
                    ) : !taskData.hasMore ? (
                      <div className={styles.loadingMore}>没有更多了</div>
                    ) : null}
                  </>
                )}
                </div>
              </div>
          </TabPane>

          <TabPane key="projects" title="项目">
            <div className={styles.projectTabContent}>
              <div ref={projectScrollRef} className={styles.scrollPanel} onScroll={handleProjectScroll}>
              {projectLoading ? (
                <div className={styles.centerSpin}>
                  <Spin />
                </div>
              ) : projectData.items.length === 0 ? (
                <Empty description="暂无项目" className={styles.empty} />
              ) : (
                <>
                  <List
                    bordered={false}
                    hoverable
                    dataSource={projectData.items}
                    render={renderProjectItem}
                  />
                  <div ref={projectSentinelRef} className={styles.sentinel} />
                  {projectLoadingMore ? (
                    <div className={styles.loadingMore}>加载中...</div>
                  ) : !projectData.hasMore ? (
                    <div className={styles.loadingMore}>没有更多了</div>
                  ) : null}
                </>
              )}
              </div>
            </div>
          </TabPane>
        </Tabs>
      </div>
    </Modal>
  );
}
